import React, { useContext, useMemo } from 'react';
import { JiraEntryPointContainer } from '@atlassian/jira-entry-point-container/src/index.tsx';
import type { RuntimePropsOfEntryPoint } from '@atlassian/jira-entry-point/src/common/types.tsx';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { ContextualAnalyticsData } from '@atlassian/jira-product-analytics-bridge';
import { PanelDataProvider } from '@atlassian/jira-software-backlog-panel/src/context/use-panel-data.tsx';
import type { versionsPanelEntryPoint } from '@atlassian/jira-software-version-backlog-panel-relay/entrypoint.tsx';
import { ANALYTICS_SOURCE_VERSION_PANEL } from '@atlassian/jira-software-version-backlog-panel-relay/src/common/constants.tsx';
import { LoadingSkeleton } from '@atlassian/jira-software-version-backlog-panel-relay/src/common/ui/loading-skeleton/index.tsx';
import {
	useViewVersionPanelExperienceTracker,
	VersionBacklogPanelExperienceStart,
} from '@atlassian/jira-software-version-backlog-panel-relay/src/services/experience-tracker/index.tsx';
import { VersionPanelEntrypointContext } from '../../../services/versions-panel-entrypoint/index.tsx';
import { useBacklogSelector } from '../../../state/index.tsx';
import { getSourceDroppableType } from '../../../state/selectors/drag/index.tsx';
import { useIsCMPBacklog } from '../../../state/state-hooks/capabilities.tsx';
import { useIsVersionsPanelOpen } from '../../../state/state-hooks/use-is-versions-panel-open.tsx';
import { PanelWrapper } from '../epic-panel/index.tsx';

export type Props = Omit<RuntimePropsOfEntryPoint<typeof versionsPanelEntryPoint>, 'onReady'>;

export const BacklogVersionsPanelWithEntrypoint = (props: Props) => {
	const { entryPointReferenceSubject, stopMetric } = useContext(VersionPanelEntrypointContext);
	const isVersionsOpen = useIsVersionsPanelOpen();

	const runtimeProps = useMemo(
		() => ({
			...props,
			onReady: stopMetric,
		}),
		[props, stopMetric],
	);
	const isCompanyManaged = useIsCMPBacklog();
	const sourceDroppableType = useBacklogSelector(getSourceDroppableType);
	return isVersionsOpen ? (
		<PanelWrapper>
			<ContextualAnalyticsData
				attributes={{
					analyticsSource: ANALYTICS_SOURCE_VERSION_PANEL,
				}}
			>
				<PanelDataProvider
					sourceDroppableType={sourceDroppableType}
					isCompanyManaged={isCompanyManaged}
					boardId={props.boardId}
				>
					<JiraEntryPointContainer
						entryPointReferenceSubject={entryPointReferenceSubject}
						id="jira.backlog.version.panel"
						packageName="jiraSoftwareVersionBacklogPanelRelay"
						errorFallback="flag"
						fallback={<LoadingSkeleton />}
						runtimeProps={runtimeProps}
					/>
				</PanelDataProvider>
			</ContextualAnalyticsData>
		</PanelWrapper>
	) : null;
};

export const VersionsPanelWithErrorBoundary = (props: Props) => {
	const { fireErrorBoundaryExperienceFail } = useViewVersionPanelExperienceTracker({
		isClassic: props.isCompanyManaged,
	});
	return (
		<>
			<VersionBacklogPanelExperienceStart experienceId={`${props.boardId}`} />
			<JSErrorBoundary
				id="backlogContentVersionsPanel"
				packageName="software-backlog"
				fallback="flag"
				onError={fireErrorBoundaryExperienceFail}
			>
				<BacklogVersionsPanelWithEntrypoint {...props} />
			</JSErrorBoundary>
		</>
	);
};
